import { usePagination } from 'components/Table/usePagination'
import React from 'react'
import { useSearchParams } from 'react-router-dom'

export const useFilter = <T extends Record<string, string | number | boolean | null>>(
  initialParams?: T,
): {
  values: T & { page: number; limit: number }
  setValue: (key: keyof T, value: string | number | boolean | null) => void
  clearValue: (key: keyof T) => void
  resetAllInitialValues: () => void
  setMultipleValues: (values: Partial<T>) => void
  clearMultipleValues: (keys: string[]) => void
} => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentPage, limit } = usePagination()

  const setValue = (key: keyof T, value: string | number | boolean | null) => {
    setSearchParams(
      { ...Object.fromEntries(searchParams), [key]: value.toString() },
      { replace: true },
    )
  }

  const clearValue = (key: keyof T) => {
    setSearchParams({ ...Object.fromEntries(searchParams), [key]: undefined }, { replace: true })
  }

  const resetAllInitialValues = () => {
    for (const key in initialParams) {
      clearValue(key as keyof T)
    }
  }

  const setMultipleValues = (values: Partial<T>) => {
    setSearchParams({ ...Object.fromEntries(searchParams), ...values }, { replace: true })
  }

  const clearMultipleValues = (keys: string[]) => {
    const newParams = { ...Object.fromEntries(searchParams) }
    delete newParams[keys[0]]
    delete newParams[keys[1]]
    setSearchParams(newParams, { replace: true })
  }

  return {
    values: {
      ...initialParams,
      ...Object.fromEntries(searchParams.entries()),
      page: currentPage,
      limit,
    },
    setValue,
    clearValue,
    resetAllInitialValues,
    setMultipleValues,
    clearMultipleValues,
  }
}
